import { useMemo } from "react";
import { useLocalization } from "@/context/localization-context";
import { errorMessage, useMonitoringCameras, useMonitoringMetrics } from "../api";
import { ErrorBlock, FetchingDot, LoadingBlock, SectionCard } from "../components/primitives";
import { CameraCharts } from "./cameras/charts";
import { CameraList } from "./cameras/camera-list";
import { cameraRow, compareRows } from "./cameras/model";
import { CamerasSummary } from "./cameras/summary";

/**
 * Cameras: which cameras are watched and online, which of them have stuck or never photographed pizzas,
 * failed triggers, refused uploads or a restart nobody picked up, and the capture / upload charts.
 */
export function CamerasTab() {
  const { t } = useLocalization();
  const cameras = useMonitoringCameras();
  const metrics = useMonitoringMetrics();
  const section = cameras.data;

  const rows = useMemo(() => (section?.cameras ?? []).map(cameraRow).sort(compareRows), [section]);
  const retry = () => void cameras.refetch();

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {section ? (
        <>
          {cameras.isError && <ErrorBlock message={errorMessage(cameras.error)} onRetry={retry} />}
          {section.error && <ErrorBlock message={t("monitoring.finding.thingsboard_unreachable", { error: section.error })} onRetry={retry} />}
          <CamerasSummary section={section} rows={rows} />
          <SectionCard
            title={t("monitoring.cameras.list.title")}
            description={t("monitoring.cameras.list.description")}
            actions={<FetchingDot active={cameras.isFetching} />}
            contentClassName="p-3 sm:p-4"
          >
            <CameraList rows={rows} />
          </SectionCard>
        </>
      ) : cameras.isError ? (
        <ErrorBlock message={errorMessage(cameras.error)} onRetry={retry} />
      ) : (
        <div className="flex flex-col gap-3">
          <LoadingBlock height={84} />
          <LoadingBlock height={320} />
        </div>
      )}
      <CameraCharts query={metrics} />
    </div>
  );
}
